import { z } from 'zod/v4';

import type { ExchangeId } from './common';

export const CURRENT_PRODUCTION_PERPS_PROVIDER_IDS = [
  'hyperliquid',
  'synthetix',
  'lighter',
] as const;

export const CURRENT_TEST_PERPS_PROVIDER_IDS = [
  ...CURRENT_PRODUCTION_PERPS_PROVIDER_IDS,
  'mock',
] as const;

export const MARKET_PRODUCT_KINDS = ['perp', 'spot', 'outcome'] as const;

export const PROVIDER_ENVIRONMENTS = ['production', 'test'] as const;

export const CAPABILITY_DECISION_STATES = [
  'supported',
  'not_applicable',
  'fixture_gated',
  'unsupported',
] as const;

export const SUPPORT_IDENTIFIER_KINDS = [
  'protocol_coin',
  'spot_pair_index',
  'action_asset_id',
  'base_token_index',
  'base_token_id',
  'quote_token_index',
  'quote_token_id',
  'hip4_encoding',
] as const;

export const PROVIDER_CAPABILITY_NAMES = [
  'directDeposit',
  'swidgeDeposit',
  'withdraw',
  'usdClassTransfer',
  'spotSend',
  'sendAsset',
  'marketOrder',
  'limitOrder',
  'triggerOrder',
  'tpSl',
  'reduceOnly',
  'reverse',
  'close',
  'scale',
  'twap',
  'twapRandomization',
  'postOnly',
  'cashback',
  'topOfBook',
  'advancedOrderParentLifecycle',
] as const;

export const PROVIDER_PRODUCT_CAPABILITY_GROUP_NAMES = [
  'marketDiscovery',
  'marketData',
  'accountData',
  'execution',
  'advancedOrders',
  'moneyMovement',
  'feesAnalytics',
  'lifecycleActions',
] as const;

export const PROVIDER_SPOT_QUOTE_SCOPES = ['USDC', 'non-USDC'] as const;

export const PROVIDER_EXECUTION_SCOPES = [
  'all_markets',
  'usdc_quote',
  'non_usdc_quote',
  'extension_lock',
] as const;

export const CAPABILITY_REASON_CODES = [
  'provider_not_registered',
  'provider_test_only',
  'product_not_registered',
  'fixture_only',
  'wrong_collateral_asset',
  'wrong_collateral_chain',
  'wallet_kind_unsupported',
  'provider_account_required',
  'amount_below_minimum',
  'feature_not_supported',
  'provider_environment_unavailable',
  'not_applicable_to_product',
] as const;

export const HYPERLIQUID_PERP_UNIVERSES = ['main'] as const;

export type ProductionPerpsProviderId =
  (typeof CURRENT_PRODUCTION_PERPS_PROVIDER_IDS)[number];

export type PerpsProviderId = ExchangeId;

export type MarketProductKind = (typeof MARKET_PRODUCT_KINDS)[number];

export type ProviderEnvironment = (typeof PROVIDER_ENVIRONMENTS)[number];

export type CapabilityDecisionState =
  (typeof CAPABILITY_DECISION_STATES)[number];

export type SupportIdentifierKind = (typeof SUPPORT_IDENTIFIER_KINDS)[number];

export type SupportIdentifier = {
  kind: SupportIdentifierKind;
  value: string;
  label?: string;
};

export type Freshness = {
  observedAt?: string;
  staleSince?: string;
  generation?: number;
};

export type ProviderCapabilityName = (typeof PROVIDER_CAPABILITY_NAMES)[number];

export type ProviderProductCapabilityGroupName =
  (typeof PROVIDER_PRODUCT_CAPABILITY_GROUP_NAMES)[number];

export type ProviderSpotQuoteScope =
  (typeof PROVIDER_SPOT_QUOTE_SCOPES)[number];

export type ProviderExecutionScope = (typeof PROVIDER_EXECUTION_SCOPES)[number];

export type CapabilityReasonCode = (typeof CAPABILITY_REASON_CODES)[number];

export type CapabilityReasonScope =
  | 'provider'
  | 'environment'
  | 'market'
  | 'product'
  | 'action'
  | 'collateral'
  | 'wallet'
  | 'account'
  | 'fixture';

export type UnsupportedReasonCategory =
  | 'registration'
  | 'product_scope'
  | 'collateral_policy'
  | 'wallet_policy'
  | 'account_state'
  | 'provider_feature'
  | 'fixture_gate';

export type UnsupportedReasonRemediation =
  | 'choose_supported_provider'
  | 'choose_supported_product'
  | 'choose_supported_asset'
  | 'choose_supported_chain'
  | 'use_eoa_wallet'
  | 'create_provider_account'
  | 'increase_amount'
  | 'wait_for_future_support'
  | 'none';

export type CapabilityUnsupportedReason = {
  code: CapabilityReasonCode;
  message: string;
  scope: CapabilityReasonScope;
  category: UnsupportedReasonCategory;
  remediation: UnsupportedReasonRemediation;
  details?: readonly string[];
};

export type CapabilitySupportedDecision = {
  state: 'supported';
  constraints?: readonly string[];
  route?: string;
  metadata?: Readonly<Record<string, unknown>>;
};

export type CapabilityNotApplicableDecision = {
  state: 'not_applicable';
  reason: string;
  reasonCode: CapabilityReasonCode;
};

export type CapabilityFixtureGatedDecision = {
  state: 'fixture_gated';
  fixture: string;
  reason: string;
  reasonCode: CapabilityReasonCode;
  structuralReason: CapabilityUnsupportedReason;
};

export type CapabilityUnsupportedDecision = {
  state: 'unsupported';
  reason: string;
  reasonCode: CapabilityReasonCode;
  structuralReason: CapabilityUnsupportedReason;
};

export type CapabilityDecision =
  | CapabilitySupportedDecision
  | CapabilityNotApplicableDecision
  | CapabilityFixtureGatedDecision
  | CapabilityUnsupportedDecision;

export type MarketCapabilityDecisions = Record<
  MarketProductKind,
  CapabilityDecision
>;

export type HyperliquidPerpAssetIdentity = {
  productKind: 'perp';
  universe: 'main' | string;
  assetIndex: number;
  dex?: string;
  localUniverseIndex?: number;
};

export type HyperliquidSpotTokenEvmContract = {
  address: string;
  evm_extra_wei_decimals: number;
};

export type HyperliquidSpotTokenMetadata = {
  index: number;
  name: string;
  fullName?: string | null;
  tokenId: string;
  isCanonical?: boolean;
  szDecimals: number;
  weiDecimals?: number;
  evmContract?: HyperliquidSpotTokenEvmContract | null;
};

export type SpotTokenIdentity = {
  tokenIndex: number;
  tokenId: string;
  symbol: string;
  name?: string;
  decimals?: number;
  /**
   * EVM-side contract for the token, when the provider exposes one. Used to
   * render an explorer link from the spot balances panel.
   */
  evmContract?: HyperliquidSpotTokenEvmContract | null;
};

export type HyperliquidSpotAssetIdentity = {
  productKind: 'spot';
  protocolCoin: `@${number}`;
  spotPairIndex: number;
  actionAssetId: number;
  baseToken: SpotTokenIdentity;
  quoteToken: SpotTokenIdentity & { symbol: 'USDC' };
  supportIdentifiers: readonly SupportIdentifier[];
};

export type HyperliquidOutcomeAssetIdentity = {
  productKind: 'outcome';
  hip4AssetId: `#${string}`;
  tokenId: `+${string}`;
  encoding: number;
  actionAssetId: number;
  collateralToken: 'USDC';
  supportIdentifiers: readonly SupportIdentifier[];
};

export type HyperliquidAssetIdentity =
  | HyperliquidPerpAssetIdentity
  | HyperliquidSpotAssetIdentity
  | HyperliquidOutcomeAssetIdentity;

export type MarketIdentity = {
  providerId: PerpsProviderId;
  productKind: MarketProductKind;
  symbol: string;
  providerMarketId: string;
  assetIdentity?: HyperliquidAssetIdentity | Readonly<Record<string, unknown>>;
};

export type ProviderCollateralMetadata = {
  asset: string;
  symbol: string;
  decimals: number;
  chain?: string;
  chainId?: number;
  tokenAddress?: string;
  assetId?: string;
  name?: string;
  usePermit2?: boolean;
};

export type ProviderLimitMetadata = {
  minimumDepositUsd?: string;
  minimumWithdrawalUsd?: string;
  minimumOrderNotionalUsd?: string;
};

export type ProviderRiskPolicyMetadata = {
  close: CapabilityDecision;
  reverse: CapabilityDecision;
  tpSl: CapabilityDecision;
  reduceOnly: CapabilityDecision;
  indeterminateActionPolicy?: 'requires_review' | 'fail_closed';
  knownProtectionCancelFailurePolicy?: 'fail_closed';
};

export type AdvancedOrderLifecycleOwnership =
  | 'provider_native'
  | 'app_orchestrated'
  | 'unsupported';

export type AdvancedOrderCapabilityDetail = {
  capability: CapabilityDecision;
  parentLifecycle: CapabilityDecision;
  ownership: AdvancedOrderLifecycleOwnership;
};

export type AdvancedOrderCapabilityMetadata = {
  scale: AdvancedOrderCapabilityDetail;
  twap: AdvancedOrderCapabilityDetail;
  twapRandomization: AdvancedOrderCapabilityDetail;
};

export type ProviderProductCapabilityGroups = {
  marketDiscovery: {
    marketCatalog: CapabilityDecision;
    marketDisplay: CapabilityDecision;
    assetIdentity: CapabilityDecision;
  };
  marketData: {
    topOfBook: CapabilityDecision;
    orderBook: CapabilityDecision;
    recentTrades: CapabilityDecision;
    candles: CapabilityDecision;
  };
  accountData: {
    balances: CapabilityDecision;
    positions: CapabilityDecision;
    openOrders: CapabilityDecision;
    fills: CapabilityDecision;
    history: CapabilityDecision;
  };
  execution: {
    marketOrder: CapabilityDecision;
    limitOrder: CapabilityDecision;
    triggerOrder: CapabilityDecision;
    postOnly: CapabilityDecision;
    reduceOnly: CapabilityDecision;
  };
  advancedOrders: {
    scale: CapabilityDecision;
    twap: CapabilityDecision;
    twapRandomization: CapabilityDecision;
    parentLifecycle: CapabilityDecision;
  };
  moneyMovement: {
    directDeposit: CapabilityDecision;
    swidgeDeposit: CapabilityDecision;
    withdraw: CapabilityDecision;
    usdClassTransfer: CapabilityDecision;
    spotSend: CapabilityDecision;
    sendAsset: CapabilityDecision;
    externalSpotDeposit: CapabilityDecision;
    externalSpotWithdrawal: CapabilityDecision;
  };
  feesAnalytics: {
    cashback: CapabilityDecision;
    fees: CapabilityDecision;
    funding: CapabilityDecision;
    liquidation: CapabilityDecision;
  };
  lifecycleActions: {
    close: CapabilityDecision;
    reverse: CapabilityDecision;
    tpSl: CapabilityDecision;
    cancel: CapabilityDecision;
    modify: CapabilityDecision;
    outcomeSplitMerge: CapabilityDecision;
  };
};

export type ProviderProductCapabilityQuoteProfile =
  ProviderProductCapabilityGroups & {
    quoteScope: ProviderSpotQuoteScope;
    executionScope?: ProviderExecutionScope;
    metadata?: Readonly<Record<string, unknown>>;
  };

export type ProviderProductCapabilitySet = ProviderProductCapabilityGroups & {
  productKind: MarketProductKind;
  executionScope?: ProviderExecutionScope;
  quoteScope?: ProviderSpotQuoteScope;
  quoteOverrides?: Partial<
    Record<ProviderSpotQuoteScope, ProviderProductCapabilityQuoteProfile>
  >;
  metadata?: Readonly<Record<string, unknown>>;
};

export type ProviderProductCapabilityMap = Record<
  MarketProductKind,
  ProviderProductCapabilitySet
>;

export type ProviderVisibilityPolicy = {
  productionRegistered: boolean;
  testOnly: boolean;
  backendPerpsRoutes: CapabilityDecision;
  depositWithdrawUi: CapabilityDecision;
  featureFlag?: string;
  currentFallbackProviderId?: ProductionPerpsProviderId;
};

export type ProviderDescriptor = {
  providerId: PerpsProviderId;
  runtimeId: PerpsProviderId;
  displayName: string;
  environment: ProviderEnvironment;
  productKinds: readonly MarketProductKind[];
  visibility: ProviderVisibilityPolicy;
};

export type ProviderCapabilitySet = {
  providerId: PerpsProviderId;
  environment: ProviderEnvironment;
  network?: 'mainnet' | 'testnet';
  markets: MarketCapabilityDecisions;
  productCapabilities: ProviderProductCapabilityMap;
  directDeposit: CapabilityDecision;
  swidgeDeposit: CapabilityDecision;
  withdraw: CapabilityDecision;
  usdClassTransfer: CapabilityDecision;
  spotSend: CapabilityDecision;
  sendAsset: CapabilityDecision;
  marketOrder: CapabilityDecision;
  limitOrder: CapabilityDecision;
  triggerOrder: CapabilityDecision;
  tpSl: CapabilityDecision;
  reduceOnly: CapabilityDecision;
  reverse: CapabilityDecision;
  close: CapabilityDecision;
  scale: CapabilityDecision;
  twap: CapabilityDecision;
  twapRandomization: CapabilityDecision;
  postOnly: CapabilityDecision;
  cashback: CapabilityDecision;
  topOfBook: CapabilityDecision;
  advancedOrderParentLifecycle: CapabilityDecision;
  collateral: readonly ProviderCollateralMetadata[];
  limits: ProviderLimitMetadata;
  riskPolicy: ProviderRiskPolicyMetadata;
  advancedOrders: AdvancedOrderCapabilityMetadata;
  visibility: ProviderVisibilityPolicy;
};

export type ProviderMarketCapability = {
  identity: MarketIdentity;
  capabilities: ProviderCapabilitySet;
};

export type PerpsProviderRegistry = {
  getProvider(providerId: PerpsProviderId): ProviderCapabilitySet;
  getMarket(identity: MarketIdentity): ProviderMarketCapability;
  requireCapability(
    identity: MarketIdentity,
    capability: ProviderCapabilityName
  ): CapabilityDecision;
};

export const perpsProviderIdSchema = z.enum(CURRENT_TEST_PERPS_PROVIDER_IDS);

export const productionPerpsProviderIdSchema = z.enum(
  CURRENT_PRODUCTION_PERPS_PROVIDER_IDS
);

export const marketProductKindSchema = z.enum(MARKET_PRODUCT_KINDS);

export const supportIdentifierSchema = z.object({
  kind: z.enum(SUPPORT_IDENTIFIER_KINDS),
  value: z.string(),
  label: z.string().optional(),
}) satisfies z.ZodType<SupportIdentifier>;

export const freshnessSchema = z.object({
  observedAt: z.string().optional(),
  staleSince: z.string().optional(),
  generation: z.number().int().nonnegative().optional(),
}) satisfies z.ZodType<Freshness>;

export const providerEnvironmentSchema = z.enum(PROVIDER_ENVIRONMENTS);

export const providerProductCapabilityGroupNameSchema = z.enum(
  PROVIDER_PRODUCT_CAPABILITY_GROUP_NAMES
);

export const providerSpotQuoteScopeSchema = z.enum(PROVIDER_SPOT_QUOTE_SCOPES);

export const providerExecutionScopeSchema = z.enum(PROVIDER_EXECUTION_SCOPES);

export const capabilityReasonCodeSchema = z.enum(CAPABILITY_REASON_CODES);

export const capabilityUnsupportedReasonSchema = z.object({
  code: capabilityReasonCodeSchema,
  message: z.string(),
  scope: z.enum([
    'provider',
    'environment',
    'market',
    'product',
    'action',
    'collateral',
    'wallet',
    'account',
    'fixture',
  ]),
  category: z.enum([
    'registration',
    'product_scope',
    'collateral_policy',
    'wallet_policy',
    'account_state',
    'provider_feature',
    'fixture_gate',
  ]),
  remediation: z.enum([
    'choose_supported_provider',
    'choose_supported_product',
    'choose_supported_asset',
    'choose_supported_chain',
    'use_eoa_wallet',
    'create_provider_account',
    'increase_amount',
    'wait_for_future_support',
    'none',
  ]),
  details: z.array(z.string()).optional(),
}) satisfies z.ZodType<CapabilityUnsupportedReason>;

export const capabilityDecisionSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('supported'),
    constraints: z.array(z.string()).optional(),
    route: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  }),
  z.object({
    state: z.literal('not_applicable'),
    reason: z.string(),
    reasonCode: capabilityReasonCodeSchema,
  }),
  z.object({
    state: z.literal('fixture_gated'),
    fixture: z.string(),
    reason: z.string(),
    reasonCode: capabilityReasonCodeSchema,
    structuralReason: capabilityUnsupportedReasonSchema,
  }),
  z.object({
    state: z.literal('unsupported'),
    reason: z.string(),
    reasonCode: capabilityReasonCodeSchema,
    structuralReason: capabilityUnsupportedReasonSchema,
  }),
]) satisfies z.ZodType<CapabilityDecision>;

export const marketCapabilityDecisionsSchema = z.object({
  perp: capabilityDecisionSchema,
  spot: capabilityDecisionSchema,
  outcome: capabilityDecisionSchema,
}) satisfies z.ZodType<MarketCapabilityDecisions>;

const hyperliquidSpotTokenEvmContractSchema = z.object({
  address: z.string(),
  evm_extra_wei_decimals: z.number().int(),
}) satisfies z.ZodType<HyperliquidSpotTokenEvmContract>;

export const hyperliquidSpotTokenMetadataSchema = z.object({
  index: z.number().int().nonnegative(),
  name: z.string(),
  fullName: z.string().nullable().optional(),
  tokenId: z.string(),
  isCanonical: z.boolean().optional(),
  szDecimals: z.number().int().nonnegative(),
  weiDecimals: z.number().int().nonnegative().optional(),
  evmContract: hyperliquidSpotTokenEvmContractSchema.nullable().optional(),
}) satisfies z.ZodType<HyperliquidSpotTokenMetadata>;

export const spotTokenIdentitySchema = z.object({
  tokenIndex: z.number().int().nonnegative(),
  tokenId: z.string(),
  symbol: z.string(),
  name: z.string().optional(),
  decimals: z.number().int().nonnegative().optional(),
  evmContract: hyperliquidSpotTokenEvmContractSchema.nullable().optional(),
}) satisfies z.ZodType<SpotTokenIdentity>;

export const hyperliquidSpotAssetIdentitySchema = z.object({
  productKind: z.literal('spot'),
  protocolCoin: z.custom<`@${number}`>(
    (value: unknown) => typeof value === 'string' && /^@\d+$/.test(value)
  ),
  spotPairIndex: z.number().int().nonnegative(),
  actionAssetId: z.number().int().nonnegative(),
  baseToken: spotTokenIdentitySchema,
  quoteToken: spotTokenIdentitySchema.extend({
    symbol: z.literal('USDC'),
  }),
  supportIdentifiers: z.array(supportIdentifierSchema).readonly(),
}) satisfies z.ZodType<HyperliquidSpotAssetIdentity>;

export const hyperliquidOutcomeAssetIdentitySchema = z.object({
  productKind: z.literal('outcome'),
  hip4AssetId: z.custom<`#${string}`>(
    (value: unknown) => typeof value === 'string' && value.startsWith('#')
  ),
  tokenId: z.custom<`+${string}`>(
    (value: unknown) => typeof value === 'string' && value.startsWith('+')
  ),
  encoding: z.number().int(),
  actionAssetId: z.number().int().nonnegative(),
  collateralToken: z.literal('USDC'),
  supportIdentifiers: z.array(supportIdentifierSchema).readonly(),
}) satisfies z.ZodType<HyperliquidOutcomeAssetIdentity>;

export const hyperliquidAssetIdentitySchema = z.discriminatedUnion(
  'productKind',
  [
    z.object({
      productKind: z.literal('perp'),
      universe: z.string(),
      assetIndex: z.number().int().nonnegative(),
      dex: z.string().optional(),
      localUniverseIndex: z.number().int().nonnegative().optional(),
    }),
    hyperliquidSpotAssetIdentitySchema,
    hyperliquidOutcomeAssetIdentitySchema,
  ]
) satisfies z.ZodType<HyperliquidAssetIdentity>;

export const marketIdentitySchema = z.object({
  providerId: perpsProviderIdSchema,
  productKind: marketProductKindSchema,
  symbol: z.string(),
  providerMarketId: z.string(),
  assetIdentity: z
    .union([hyperliquidAssetIdentitySchema, z.record(z.string(), z.unknown())])
    .optional(),
}) satisfies z.ZodType<MarketIdentity>;

export const providerCollateralMetadataSchema = z.object({
  asset: z.string(),
  symbol: z.string(),
  decimals: z.number().int().nonnegative(),
  chain: z.string().optional(),
  chainId: z.number().int().positive().optional(),
  tokenAddress: z.string().optional(),
  assetId: z.string().optional(),
  name: z.string().optional(),
  usePermit2: z.boolean().optional(),
}) satisfies z.ZodType<ProviderCollateralMetadata>;

export const providerLimitMetadataSchema = z.object({
  minimumDepositUsd: z.string().optional(),
  minimumWithdrawalUsd: z.string().optional(),
  minimumOrderNotionalUsd: z.string().optional(),
}) satisfies z.ZodType<ProviderLimitMetadata>;

export const providerRiskPolicyMetadataSchema = z.object({
  close: capabilityDecisionSchema,
  reverse: capabilityDecisionSchema,
  tpSl: capabilityDecisionSchema,
  reduceOnly: capabilityDecisionSchema,
  indeterminateActionPolicy: z
    .enum(['requires_review', 'fail_closed'])
    .optional(),
  knownProtectionCancelFailurePolicy: z.literal('fail_closed').optional(),
}) satisfies z.ZodType<ProviderRiskPolicyMetadata>;

export const advancedOrderCapabilityDetailSchema = z.object({
  capability: capabilityDecisionSchema,
  parentLifecycle: capabilityDecisionSchema,
  ownership: z.enum(['provider_native', 'app_orchestrated', 'unsupported']),
}) satisfies z.ZodType<AdvancedOrderCapabilityDetail>;

export const advancedOrderCapabilityMetadataSchema = z.object({
  scale: advancedOrderCapabilityDetailSchema,
  twap: advancedOrderCapabilityDetailSchema,
  twapRandomization: advancedOrderCapabilityDetailSchema,
}) satisfies z.ZodType<AdvancedOrderCapabilityMetadata>;

export const providerProductCapabilityGroupsSchema = z.object({
  marketDiscovery: z.object({
    marketCatalog: capabilityDecisionSchema,
    marketDisplay: capabilityDecisionSchema,
    assetIdentity: capabilityDecisionSchema,
  }),
  marketData: z.object({
    topOfBook: capabilityDecisionSchema,
    orderBook: capabilityDecisionSchema,
    recentTrades: capabilityDecisionSchema,
    candles: capabilityDecisionSchema,
  }),
  accountData: z.object({
    balances: capabilityDecisionSchema,
    positions: capabilityDecisionSchema,
    openOrders: capabilityDecisionSchema,
    fills: capabilityDecisionSchema,
    history: capabilityDecisionSchema,
  }),
  execution: z.object({
    marketOrder: capabilityDecisionSchema,
    limitOrder: capabilityDecisionSchema,
    triggerOrder: capabilityDecisionSchema,
    postOnly: capabilityDecisionSchema,
    reduceOnly: capabilityDecisionSchema,
  }),
  advancedOrders: z.object({
    scale: capabilityDecisionSchema,
    twap: capabilityDecisionSchema,
    twapRandomization: capabilityDecisionSchema,
    parentLifecycle: capabilityDecisionSchema,
  }),
  moneyMovement: z.object({
    directDeposit: capabilityDecisionSchema,
    swidgeDeposit: capabilityDecisionSchema,
    withdraw: capabilityDecisionSchema,
    usdClassTransfer: capabilityDecisionSchema,
    spotSend: capabilityDecisionSchema,
    sendAsset: capabilityDecisionSchema,
    externalSpotDeposit: capabilityDecisionSchema,
    externalSpotWithdrawal: capabilityDecisionSchema,
  }),
  feesAnalytics: z.object({
    cashback: capabilityDecisionSchema,
    fees: capabilityDecisionSchema,
    funding: capabilityDecisionSchema,
    liquidation: capabilityDecisionSchema,
  }),
  lifecycleActions: z.object({
    close: capabilityDecisionSchema,
    reverse: capabilityDecisionSchema,
    tpSl: capabilityDecisionSchema,
    cancel: capabilityDecisionSchema,
    modify: capabilityDecisionSchema,
    outcomeSplitMerge: capabilityDecisionSchema,
  }),
}) satisfies z.ZodType<ProviderProductCapabilityGroups>;

export const providerProductCapabilityQuoteProfileSchema =
  providerProductCapabilityGroupsSchema.extend({
    quoteScope: providerSpotQuoteScopeSchema,
    executionScope: providerExecutionScopeSchema.optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  }) satisfies z.ZodType<ProviderProductCapabilityQuoteProfile>;

export const providerProductCapabilitySetSchema =
  providerProductCapabilityGroupsSchema.extend({
    productKind: marketProductKindSchema,
    executionScope: providerExecutionScopeSchema.optional(),
    quoteScope: providerSpotQuoteScopeSchema.optional(),
    quoteOverrides: z
      .object({
        USDC: providerProductCapabilityQuoteProfileSchema.optional(),
        'non-USDC': providerProductCapabilityQuoteProfileSchema.optional(),
      })
      .optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  }) satisfies z.ZodType<ProviderProductCapabilitySet>;

export const providerProductCapabilityMapSchema: z.ZodType<ProviderProductCapabilityMap> =
  z.object({
    perp: providerProductCapabilitySetSchema,
    spot: providerProductCapabilitySetSchema,
    outcome: providerProductCapabilitySetSchema,
  });

export const providerVisibilityPolicySchema = z.object({
  productionRegistered: z.boolean(),
  testOnly: z.boolean(),
  backendPerpsRoutes: capabilityDecisionSchema,
  depositWithdrawUi: capabilityDecisionSchema,
  featureFlag: z.string().optional(),
  currentFallbackProviderId: productionPerpsProviderIdSchema.optional(),
}) satisfies z.ZodType<ProviderVisibilityPolicy>;

export const providerDescriptorSchema = z.object({
  providerId: perpsProviderIdSchema,
  runtimeId: perpsProviderIdSchema,
  displayName: z.string(),
  environment: providerEnvironmentSchema,
  productKinds: z.array(marketProductKindSchema),
  visibility: providerVisibilityPolicySchema,
}) satisfies z.ZodType<ProviderDescriptor>;

export const providerCapabilitySetSchema = z.object({
  providerId: perpsProviderIdSchema,
  environment: providerEnvironmentSchema,
  network: z.enum(['mainnet', 'testnet']).optional(),
  markets: marketCapabilityDecisionsSchema,
  productCapabilities: providerProductCapabilityMapSchema,
  directDeposit: capabilityDecisionSchema,
  swidgeDeposit: capabilityDecisionSchema,
  withdraw: capabilityDecisionSchema,
  usdClassTransfer: capabilityDecisionSchema,
  spotSend: capabilityDecisionSchema,
  sendAsset: capabilityDecisionSchema,
  marketOrder: capabilityDecisionSchema,
  limitOrder: capabilityDecisionSchema,
  triggerOrder: capabilityDecisionSchema,
  tpSl: capabilityDecisionSchema,
  reduceOnly: capabilityDecisionSchema,
  reverse: capabilityDecisionSchema,
  close: capabilityDecisionSchema,
  scale: capabilityDecisionSchema,
  twap: capabilityDecisionSchema,
  twapRandomization: capabilityDecisionSchema,
  postOnly: capabilityDecisionSchema,
  cashback: capabilityDecisionSchema,
  topOfBook: capabilityDecisionSchema,
  advancedOrderParentLifecycle: capabilityDecisionSchema,
  collateral: z.array(providerCollateralMetadataSchema),
  limits: providerLimitMetadataSchema,
  riskPolicy: providerRiskPolicyMetadataSchema,
  advancedOrders: advancedOrderCapabilityMetadataSchema,
  visibility: providerVisibilityPolicySchema,
}) satisfies z.ZodType<ProviderCapabilitySet>;
